import axios from 'axios'
import { API_BASE_URL, getStoredBearerToken } from './api'

// ── Who we wake (BRIEF-A10) ─────────────────────────────────────────
//
// The owner's ordered list: position 1 is always the owner (their verified
// mobile), position 2 an optional backup person. Mounted unprefixed at
// /auth/v2/who-we-wake (no /api/v1), like the owner's own phone check. Only
// an owner may call these; a member gets 403. Numbers come back as their
// last three digits only, never in full.

export interface WakePerson {
  position: 1 | 2
  role: 'owner' | 'backup'
  name: string | null
  ending: string | null
  verified: boolean
}

export interface WhoWeWake {
  people: WakePerson[]
}

function url(path = ''): string {
  return `${API_BASE_URL}/auth/v2/who-we-wake${path}`
}

function headers(): Record<string, string> {
  const token = getStoredBearerToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export async function getWhoWeWake(): Promise<WhoWeWake> {
  const response = await axios.get<WhoWeWake>(url(), {
    headers: headers(),
    timeout: 30000,
  })
  return response.data
}

// Adds the one backup (or sends a fresh code to the backup already added).
// A code is texted to the backup's mobile; the owner enters it here.
export async function addWakeBackup(
  name: string,
  phoneNumber: string,
): Promise<WhoWeWake> {
  const response = await axios.post<WhoWeWake>(
    url('/backup'),
    { name, phone_number: phoneNumber },
    { headers: headers(), timeout: 30000 },
  )
  return response.data
}

// "Send a new code": the same add route with no name or number, so a new
// code goes to the backup already added (the backend keeps their number).
export async function resendWakeBackupCode(): Promise<WhoWeWake> {
  const response = await axios.post<WhoWeWake>(
    url('/backup'),
    {},
    { headers: headers(), timeout: 30000 },
  )
  return response.data
}

export async function verifyWakeBackup(code: string): Promise<WhoWeWake> {
  const response = await axios.post<WhoWeWake>(
    url('/backup/verify'),
    { code },
    { headers: headers(), timeout: 30000 },
  )
  return response.data
}

export async function removeWakeBackup(): Promise<WhoWeWake> {
  const response = await axios.delete<WhoWeWake>(url('/backup'), {
    headers: headers(),
    timeout: 30000,
  })
  return response.data
}
